import React, { useEffect, useState, useCallback } from 'react';
import TerminalCard from '../components/TerminalCard';
import { getMyTerminals, updateTerminalProfile } from '../services/api';
import { FaSync, FaServer, FaExclamationTriangle, FaCog, FaSpinner, FaTimes } from 'react-icons/fa';

const ProfileConfigModal = ({ terminal, onSave, onClose }) => {
  const [tipoCultivo, setTipoCultivo] = useState(terminal.tipo_cultivo || '');
  const [tipoSuelo, setTipoSuelo] = useState(terminal.tipo_suelo || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(terminal.guid, tipoCultivo, tipoSuelo);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-900">Configurar Perfil Agronómico</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <FaTimes className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">Terminal: <span className="font-medium text-gray-800">{terminal.guid}</span></p>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Cultivo</label>
            <select value={tipoCultivo} onChange={e => setTipoCultivo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none">
              <option value="">Sin configurar</option>
              <option value="Uva">Uva</option>
              <option value="Cerezo">Cerezo</option>
              <option value="Arandano">Arándano</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Tipo de Suelo</label>
            <select value={tipoSuelo} onChange={e => setTipoSuelo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none">
              <option value="">Sin configurar</option>
              <option value="FrancoArenoso">Franco Arenoso</option>
              <option value="Franco">Franco</option>
              <option value="Arcilloso">Arcilloso</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm">
            {saving && <FaSpinner className="w-3.5 h-3.5 animate-spin" />}
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SOIL_LABELS = { FrancoArenoso: 'F. Arenoso', Franco: 'Franco', Arcilloso: 'Arcilloso' };

const TerminalesPage = ({ token, user }) => {
  const [terminales, setTerminales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [configTarget, setConfigTarget] = useState(null);

  const fetchTerminales = useCallback(async (isRefresh = false) => {
    if (!token) return;
    if (isRefresh) setRefreshing(true);
    try {
      const res = await getMyTerminals(token);
      if (res.cod === 200 && Array.isArray(res.data)) {
        setTerminales(res.data);
        setError('');
      } else {
        setTerminales([]);
      }
    } catch (e) {
      setError('Error al cargar terminales');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTerminales();
  }, [fetchTerminales]);

  const handleSaveProfile = async (guid, tipoCultivo, tipoSuelo) => {
    await updateTerminalProfile(token, guid, tipoCultivo, tipoSuelo);
    setConfigTarget(null);
    fetchTerminales();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mis Terminales</h1>
          <p className="text-sm text-gray-500 mt-1">
            {user?.cliente_nombre && <><span className="font-medium text-gray-700">{user.cliente_nombre}</span> &middot; </>}
            {terminales.length} terminal{terminales.length !== 1 ? 'es' : ''} registrada{terminales.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button onClick={() => fetchTerminales(true)} disabled={refreshing} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm font-medium text-gray-700 transition-colors">
          {refreshing ? <FaSpinner className="w-3.5 h-3.5 animate-spin" /> : <FaSync className="w-3.5 h-3.5" />}
          {refreshing ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg mb-6">
          <FaExclamationTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {configTarget && (
        <ProfileConfigModal terminal={configTarget} onSave={handleSaveProfile} onClose={() => setConfigTarget(null)} />
      )}

      {terminales.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FaServer className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-1">Sin terminales</h3>
          <p className="text-sm text-gray-400">No tienes terminales asignadas aún.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {terminales.map((terminal) => (
            <div key={terminal.guid} className="relative">
              <TerminalCard terminal={terminal} />
              <button onClick={() => setConfigTarget(terminal)}
                className="absolute -top-2.5 right-3 p-1.5 bg-white border border-gray-200 rounded-lg text-gray-400 hover:text-emerald-600 hover:border-emerald-200 hover:bg-emerald-50 transition-all shadow-md z-10"
                title="Configurar perfil agronómico">
                <FaCog className="w-3.5 h-3.5" />
              </button>
              {(terminal.tipo_cultivo || terminal.tipo_suelo) ? (
                <span className="absolute bottom-3 right-3 text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 z-10">
                  {terminal.tipo_cultivo || '—'} / {terminal.tipo_suelo ? (SOIL_LABELS[terminal.tipo_suelo] || terminal.tipo_suelo) : '—'}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TerminalesPage;
